/**
 * Predefined color options available when creating or editing a tag.
 * Each entry includes Tailwind class names for background, text, dot, and ring.
 * @type {Array<{ name: string, hex: string, bg: string, text: string, dot: string, ring: string }>}
 */
export const TAG_COLORS = [
  { name: 'blue', hex: '#3B82F6', bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500', ring: 'ring-blue-500' },
  { name: 'green', hex: '#22C55E', bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500', ring: 'ring-green-500' },
  { name: 'yellow', hex: '#EAB308', bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500', ring: 'ring-yellow-500' },
  { name: 'red', hex: '#EF4444', bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500', ring: 'ring-red-500' },
  { name: 'purple', hex: '#A855F7', bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500', ring: 'ring-purple-500' },
  { name: 'pink', hex: '#EC4899', bg: 'bg-pink-100', text: 'text-pink-800', dot: 'bg-pink-500', ring: 'ring-pink-500' },
  { name: 'gray', hex: '#6B7280', bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-500', ring: 'ring-gray-500' },
  { name: 'indigo', hex: '#6366F1', bg: 'bg-indigo-100', text: 'text-indigo-800', dot: 'bg-indigo-500', ring: 'ring-indigo-500' },
  { name: 'teal', hex: '#14B8A6', bg: 'bg-teal-100', text: 'text-teal-800', dot: 'bg-teal-500', ring: 'ring-teal-500' },
  { name: 'orange', hex: '#F97316', bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500', ring: 'ring-orange-500' },
];

/**
 * Get color classes for a tag color
 * Supports both predefined color names and custom hex colors
 * @param {string} colorValue - Either a predefined color name or a hex color (e.g., '#FF5733')
 * @returns {Object} Color classes object
 */
export const getColorClasses = (colorValue) => {
  // Check if it's a hex color
  if (colorValue?.startsWith('#')) {
    // For custom hex colors, return inline style-compatible values
    return {
      name: colorValue,
      hex: colorValue,
      bg: 'bg-gray-100', // fallback bg
      text: 'text-gray-800', // fallback text
      dot: '', // Will use inline style instead
      ring: 'ring-gray-400',
      isCustom: true,
    };
  }

  // Find predefined color
  return TAG_COLORS.find(c => c.name === colorValue) || TAG_COLORS[0];
};

/**
 * Get the hex value for a color (works for both predefined and custom)
 * @param {string} colorValue - Either a predefined color name or a hex color
 * @returns {string} Hex color value
 */
export const getHexColor = (colorValue) => {
  if (colorValue?.startsWith('#')) {
    return colorValue;
  }
  const predefined = TAG_COLORS.find(c => c.name === colorValue);
  return predefined?.hex || TAG_COLORS[0].hex;
};

/** Default color name applied to newly created tags. */
export const DEFAULT_TAG_COLOR = 'blue';
